import { OnInit, OnDestroy, Component } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { LayoutService } from './service/app.layout.service';
import { PermissionService } from '../core/services/permission.service';
import { SchedulingService } from '../core/services/scheduling.service';
import { BotService } from '../core/services/bot.service';
import { UserService } from '../core/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'globot-menu',
  templateUrl: './app.menu.component.html',
})
export class AppMenuComponent implements OnInit, OnDestroy {
  model: any[] = [];
  private subscription = new Subscription();
  private schedulingEnabled = false;
  private currentBotId: string | null = null;

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    private permissionService: PermissionService,
    private schedulingService: SchedulingService,
    private botService: BotService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.buildMenu();
    this.subscription.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        this.refreshBotContext();
        this.buildMenu();
      })
    );
    this.subscription.add(this.permissionService.currentRole$.subscribe(() => this.buildMenu()));
    this.subscription.add(
      this.schedulingService.schedulingEnabled$.subscribe((enabled) => {
        this.schedulingEnabled = enabled;
        this.buildMenu();
      })
    );
    this.subscription.add(this.botService.haveBots$.subscribe(() => this.buildMenu()));
    this.refreshBotContext();
  }

  private readonly UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  private extractBotId(url: string): string | null {
    const match = /edit-bot\/([^/?#]+)/.exec(url);
    const id = match ? match[1] : null;
    return id && this.UUID_RE.test(id) ? id : null;
  }

  private refreshBotContext(): void {
    const url = this.router.routerState.snapshot.url;
    const newBotId = this.extractBotId(url);
    if (newBotId && newBotId !== this.currentBotId) {
      this.currentBotId = newBotId;
      this.schedulingService.refreshSchedulingEnabled(newBotId);
    } else if (!newBotId) {
      this.currentBotId = null;
    }
  }

  private buildMenu(): void {
    const url = this.router.routerState.snapshot.url;
    const botId = this.extractBotId(url);

    if (botId) {
      const botBase = `/dashboard/edit-bot/${botId}`;
      this.model = this.buildBotMenu(botBase);
    } else {
      this.model = this.buildDefaultMenu();
    }
  }

  private buildBotMenu(botBase: string): any[] {
    const can = (perm: string) => this.permissionService.hasPermission(perm);
    const canAny = (perms: string[]) => this.permissionService.hasAnyPermission(perms);

    const operationItems: any[] = [
      {
        label: 'Estadísticas',
        icon: 'pi pi-fw pi-chart-bar',
        routerLink: [`${botBase}/dashboard`],
      },
      { label: 'Chat en vivo', icon: 'pi pi-fw pi-comment', routerLink: [`${botBase}/live`] },
    ];
    operationItems.push({
      label: 'Historial',
      icon: 'pi pi-fw pi-history',
      routerLink: [`${botBase}/records`],
    });

    const configItems: any[] = [
      {
        label: 'Probar asistente',
        icon: 'pi pi-fw pi-play',
        routerLink: [`${botBase}/widget-config`],
      },
    ];
    // GLOB-1895: visibility is gated on read permissions, not write ones. Roles
    // without edit rights (AGENT, VIEWER) still reach these screens in read-only
    // mode — each screen disables its own write controls.
    if (canAny(['chatbots.view', 'chatbots.config', 'chatbots.edit'])) {
      configItems.push({
        label: 'Configuraciones',
        icon: 'pi pi-fw pi-cog',
        routerLink: [`${botBase}/config`],
      });
    }
    if (canAny(['datasources.view', 'datasources.create', 'datasources.edit'])) {
      configItems.push({
        label: 'Carga de información',
        icon: 'pi pi-fw pi-upload',
        routerLink: [`${botBase}/datasource`],
      });
    }

    const channelItems: any[] = [];
    if (can('integrations.edit') || can('integrations.create') || can('integrations.view')) {
      channelItems.push(
        {
          label: 'Agregar a sitio web',
          icon: 'pi pi-fw pi-globe',
          routerLink: [`${botBase}/embed`],
        },
        { label: 'Whatsapp', icon: 'pi pi-fw pi-whatsapp', routerLink: [`${botBase}/whatsapp`] },
        { label: 'Messenger', icon: 'pi pi-fw pi-facebook', routerLink: [`${botBase}/messenger`] },
        { label: 'Instagram', icon: 'pi pi-fw pi-instagram', routerLink: [`${botBase}/instagram`] }
      );
    }

    const schedulingItems: any[] = [];
    // Visibility gated on read permission, not write — same as Configuraciones above.
    if (
      environment.schedulingEnabled &&
      canAny(['scheduling.view', 'scheduling.config', 'scheduling.manage'])
    ) {
      schedulingItems.push({
        label: 'Panel',
        icon: 'pi pi-fw pi-th-large',
        routerLink: [`${botBase}/scheduling`],
      });
      if (this.schedulingEnabled) {
        schedulingItems.push(
          {
            label: 'Servicios',
            icon: 'pi pi-fw pi-list',
            routerLink: [`${botBase}/scheduling/services`],
          },
          {
            label: 'Sucursales',
            icon: 'pi pi-fw pi-building',
            routerLink: [`${botBase}/scheduling/branches`],
          },
          {
            label: 'Profesionales',
            icon: 'pi pi-fw pi-users',
            routerLink: [`${botBase}/scheduling/resources`],
          },
          {
            label: 'Horarios',
            icon: 'pi pi-fw pi-clock',
            routerLink: [`${botBase}/scheduling/schedules`],
          },
          {
            label: 'Citas',
            icon: 'pi pi-fw pi-calendar',
            routerLink: [`${botBase}/scheduling/appointments`],
          },
          {
            label: 'Configuración',
            icon: 'pi pi-fw pi-cog',
            routerLink: [`${botBase}/scheduling/config`],
          }
        );
      }
    }

    const menu: any[] = [{ label: 'Operación', items: operationItems }];
    if (configItems.length > 0) {
      menu.push({ label: 'Configuración', items: configItems });
    }
    if (schedulingItems.length > 0) {
      menu.push({ label: 'Agendamiento', items: schedulingItems });
    }
    if (channelItems.length > 0) {
      menu.push({ label: 'Canales', items: channelItems });
    }
    return menu;
  }

  private buildDefaultMenu(): any[] {
    const can = (perm: string) => this.permissionService.hasPermission(perm);
    const user = this.userService.getUserLocalStorage();
    const isOnboarding = !this.botService.haveBots() && !!user?.on_boarding;

    const disabledProps = isOnboarding ? { disabled: true, class: 'layout-menuitem-disabled' } : {};

    const operationItems: any[] = [
      {
        label: 'Mis asistentes',
        materialIcon: 'smart_toy',
        routerLink: ['/dashboard/bot'],
        ...disabledProps,
      },
    ];
    if (can('conversations.manage') || can('conversations.view')) {
      operationItems.push({
        label: 'Clientes',
        icon: 'pi pi-fw pi-id-card',
        routerLink: ['/dashboard/crm/clientes'],
        ...disabledProps,
      });
    }

    const configItems: any[] = [];
    configItems.push({
      label: 'Mi equipo',
      icon: 'pi pi-fw pi-users',
      routerLink: ['/dashboard/team'],
      ...disabledProps,
    });
    // Notificaciones: visible a todos los miembros del workspace.
    // La página interna gateia las acciones de configuración (OWNER/ADMIN editan,
    // el resto ve "Acceso restringido"). Mostrar siempre el item evita confusión
    // sobre dónde encontrar la sección.
    configItems.push({
      label: 'Notificaciones',
      icon: 'pi pi-fw pi-bell',
      routerLink: ['/dashboard/notifications'],
      ...disabledProps,
    });

    const menu: any[] = [{ label: 'Operación', items: operationItems }];
    if (configItems.length > 0) {
      menu.push({ label: 'Configuración', items: configItems });
    }
    return menu;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
